import { useState } from "react";
import { useNavigate } from "react-router";
import { PageHeader } from "../../components/shared/PageHeader";
import { Badge } from "../../components/shared/Badge";
import { Plus } from "lucide-react";
import { useToast } from "@nthamizh/ui";
import { usePlatformUsers, usePlatformEnterprises, useCreatePlatformUser } from "@platform/hooks";

interface PlatformUser {
  id: string;      // the user's UUID (not short_id — GET /users/:id looks up by raw id)
  name: string; email: string;
  platformRole: string | null; createdAt: string; isLocked?: boolean;
  enterpriseIds?: string[];
}

interface Enterprise { id: string; name: string; slug: string }

export function PlatformAllUsersPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [enterpriseFilter, setEnterpriseFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const { data: users = [], isLoading } = usePlatformUsers();
  const { data: enterprises = [] } = usePlatformEnterprises();
  const createUser = useCreatePlatformUser();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [addError, setAddError] = useState("");
  const [createdCredentials, setCreatedCredentials] = useState<{ email: string; tempPassword: string } | null>(null);

  const handleAddUser = async () => {
    if (!newName.trim() || !newEmail.trim()) {
      setAddError("Name and email are required.");
      return;
    }
    try {
      const result = await createUser.mutateAsync({ name: newName.trim(), email: newEmail.trim() });
      setShowAddDialog(false);
      setCreatedCredentials({ email: result.email, tempPassword: result.tempPassword });
      setNewName("");
      setNewEmail("");
      setAddError("");
      toast({ title: `${result.name} created`, variant: "success" });
    } catch (err: unknown) {
      setAddError(err instanceof Error ? err.message : "Failed to create user");
    }
  };

  const typedUsers = users as PlatformUser[];
  const typedEnterprises = enterprises as Enterprise[];

  const filtered = typedUsers.filter((u) => {
    const matchSearch =
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      statusFilter === "all" ||
      (statusFilter === "locked" && u.isLocked) ||
      (statusFilter === "active" && !u.isLocked);
    // enterpriseFilter was previously tracked in state and wired to the
    // dropdown's onChange, but never actually referenced here — the
    // dropdown looked functional (options populated, selection worked)
    // but silently filtered nothing. "no-enterprise" is its own option
    // (not just omitted) since individual users are a real, distinct
    // population an admin would want to isolate, not just an edge case
    // to fall through.
    const matchEnterprise =
      enterpriseFilter === "all" ||
      (enterpriseFilter === "none" && (u.enterpriseIds?.length ?? 0) === 0) ||
      (u.enterpriseIds?.includes(enterpriseFilter) ?? false);
    return matchSearch && matchStatus && matchEnterprise;
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="All users"
        subtitle="Every user across all enterprises"
        action={
          <button
            onClick={() => { setShowAddDialog(true); setAddError(""); }}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" /> Add user
          </button>
        }
      />

      {createdCredentials && (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
          <p className="text-xs font-semibold text-amber-700 mb-1">
            {createdCredentials.email} — temporary password
          </p>
          <p className="text-sm font-mono text-amber-900">{createdCredentials.tempPassword}</p>
          <p className="text-xs text-amber-600 mt-1">
            Share securely. The user must change it on first login. This is shown once and cannot be retrieved again —
            use &quot;Reset password&quot; on their detail page if it&apos;s lost.
          </p>
        </div>
      )}

      <div className="flex gap-3 flex-wrap">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 w-64"
        />
        <select
          value={enterpriseFilter}
          onChange={(e) => setEnterpriseFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 bg-white"
          aria-label="Filter by enterprise"
        >
          <option value="all">All enterprises</option>
          <option value="none">No enterprise (individual)</option>
          {typedEnterprises.map((e) => (
            <option key={e.id} value={e.id}>{e.name}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 bg-white"
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="locked">Locked</option>
        </select>
      </div>

      <div className="bg-white border border-ds-border rounded-card shadow-card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ds-text-muted">Loading users...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-ds-text-muted">No users found.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-ds-border bg-ds-bg">
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">User</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Platform role</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Status</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Joined</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ds-border">
              {filtered.map((u) => (
                <tr
                  key={u.id}
                  className="hover:bg-ds-bg/50 cursor-pointer"
                  onClick={() => navigate(`/platform/users/${u.id}`)}
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-semibold flex-shrink-0">
                        {u.name.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-ds-text">{u.name}</p>
                        <p className="text-xs text-ds-text-muted">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    {u.platformRole
                      ? <Badge variant="purple">{u.platformRole.replace("_", " ")}</Badge>
                      : <span className="text-xs text-ds-text-muted">—</span>}
                  </td>
                  <td className="px-5 py-3.5">
                    {u.isLocked
                      ? <Badge variant="red">Locked</Badge>
                      : <Badge variant="green">Active</Badge>}
                  </td>
                  <td className="px-5 py-3.5 text-xs text-ds-text-muted">
                    {new Date(u.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3.5 text-right text-xs text-indigo-600 font-medium">
                    Manage →
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAddDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/20 backdrop-blur-sm"
            onClick={() => setShowAddDialog(false)}
          />
          <div className="relative bg-white rounded-card shadow-card border border-ds-border p-6 w-full max-w-md space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-ds-text">Add user</h2>
              <p className="text-xs text-ds-text-muted mt-1">
                Creates an individual user with no enterprise. Assign one afterward from their detail page if needed.
              </p>
            </div>
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-ds-text-secondary">Name</label>
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => { setNewName(e.target.value); setAddError(""); }}
                  className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-ds-text-secondary">Email</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => { setNewEmail(e.target.value); setAddError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleAddUser()}
                  className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400"
                />
              </div>
              {addError && <p className="text-xs text-red-600">{addError}</p>}
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <button
                onClick={() => setShowAddDialog(false)}
                className="px-3.5 py-2 rounded-lg border border-ds-border text-sm text-ds-text-secondary hover:bg-ds-bg"
              >
                Cancel
              </button>
              <button
                onClick={handleAddUser}
                disabled={createUser.isPending || !newName.trim() || !newEmail.trim()}
                className="px-3.5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                {createUser.isPending ? "Creating..." : "Create user"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
